import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Tabs } from "@mantine/core";
import { Loader2Icon, GlobeIcon, LinkIcon } from "lucide-react";

import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import backend from "../../../../../backend/backend";
import Notify from "../../../../shared/Notify";
import { updateCurrentProject } from "../../../store/builderActionCreators";
import CustomDomainWizard from "./CustomDomainWizard";

import "./PublishContent.scss";

const PublishContent = ({ project }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const formRef = useRef(null);
  const subdomainInputRef = useRef(null);
  const lastValidSubdomainRef = useRef("");

  const [activeTab, setActiveTab] = useState("subdomain");
  const [subdomain, setSubdomain] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [pageDescription, setPageDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Set active tab based on whether project has custom domain
  useEffect(() => {
    if (project?.customDomain) {
      setActiveTab("custom");
    }
  }, [project?.customDomain]);

  // Initialize form values from project
  useEffect(() => {
    if (project) {
      // Pre-populate subdomain with project UID if subdomain is empty
      const initialSubdomain = project.subdomain || project.uid || "";
      setSubdomain(initialSubdomain);
      lastValidSubdomainRef.current = initialSubdomain;
      setPageTitle(project.pageTitle || "");
      setPageDescription(project.pageDescription || "");
    }
  }, [project]);

  // Clear error when subdomain changes
  useEffect(() => {
    setError("");
  }, [subdomain]);

  const handleSubdomainBlur = () => {
    const trimmed = subdomain.trim();
    if (!trimmed) {
      // Restore last valid value if empty
      setSubdomain(lastValidSubdomainRef.current);
    } else {
      // Update last valid value
      lastValidSubdomainRef.current = trimmed;
    }
  };

  const validateSubdomain = (value) => {
    if (!value || value.length < 5) {
      return label(intl, "publish_subdomain_min_length", "Subdomain must be at least 5 characters");
    }
    // Check for valid subdomain characters (alphanumeric and hyphens)
    if (!/^[a-z0-9-]+$/i.test(value)) {
      return label(intl, "publish_subdomain_invalid_chars", "Subdomain can only contain letters, numbers, and hyphens");
    }
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate subdomain
    const validationError = validateSubdomain(subdomain);
    if (validationError) {
      setError(validationError);
      subdomainInputRef.current?.focus();
      return;
    }

    // Check if anything changed
    const hasChanges =
      subdomain !== (project?.subdomain || "") ||
      pageTitle !== (project?.pageTitle || "") ||
      pageDescription !== (project?.pageDescription || "");

    if (!hasChanges) {
      Notify.info(label(intl, "publish_no_changes", "No changes to save"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const updatedProject = await backend.updateBuilderProject(workspaceId, project.uid, {
        subdomain,
        pageTitle: pageTitle || null,
        pageDescription: pageDescription || null,
      });

      Notify.success(label(intl, "publish_saved", "Publish settings saved"));
      // Update last valid subdomain after successful save
      lastValidSubdomainRef.current = subdomain;
      // Update project in Redux store to refresh preview
      console.log("[PublishContent] Dispatching updateCurrentProject with subdomain:", updatedProject?.subdomain);
      if (updatedProject) {
        dispatch(updateCurrentProject(updatedProject));
      }
    } catch (err) {
      if (err.json) {
        const errorJson = await err.json();
        // Check for error_code field for i18n lookup, fallback to message
        const errorCode = errorJson?.error_code || errorJson?.message;
        const errorMsg = errorCode
          ? label(intl, errorCode, errorCode)
          : label(intl, "error_saving_publish", "Failed to save publish settings");
        setError(errorMsg);
      } else {
        setError(label(intl, "error_saving_publish", "Failed to save publish settings"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!project) {
    return (
      <div className="publish_content">
        <div className="publish_loading">
          <Loader2Icon size={24} className="spinning" />
        </div>
      </div>
    );
  }

  return (
    <div className="publish_content">
      <div className="publish_form_container">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List className="publish_tabs_list">
            <Tabs.Tab value="subdomain" leftSection={<LinkIcon size={16} />}>
              <Message id="publish_tab_subdomain" defaultMessage="PromptLeo Subdomain" />
            </Tabs.Tab>
            <Tabs.Tab value="custom" leftSection={<GlobeIcon size={16} />}>
              <Message id="publish_tab_custom" defaultMessage="Custom Domain" />
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="subdomain" pt="md">
            <form ref={formRef} onSubmit={handleSave}>
              <div className="form_group">
                <label htmlFor="subdomain">
                  <Message id="publish_subdomain_label" defaultMessage="Subdomain" />
                  <span className="required">*</span>
                </label>
                <div className="subdomain_input_wrapper">
                  <input
                    ref={subdomainInputRef}
                    id="subdomain"
                    type="text"
                    className={`form-control subdomain_input ${error ? "is-invalid" : ""}`}
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                    onBlur={handleSubdomainBlur}
                    placeholder={label(intl, "publish_subdomain_placeholder", "myapp")}
                    maxLength="63"
                    disabled={loading}
                  />
                  <span className="subdomain_suffix">.promptleo.com</span>
                </div>
                {error && <div className="error_message">{error}</div>}
                <div className="form_hint">
                  <Message
                    id="publish_subdomain_hint"
                    defaultMessage="Your app will be available at {subdomain}.promptleo.com"
                    values={{ subdomain: subdomain || "myapp" }}
                  />
                </div>
              </div>

              <div className="form_group">
                <label htmlFor="pageTitle">
                  <Message id="publish_title_label" defaultMessage="Page Title" />
                </label>
                <input
                  id="pageTitle"
                  type="text"
                  className="form-control"
                  value={pageTitle}
                  onChange={(e) => setPageTitle(e.target.value)}
                  placeholder={label(intl, "publish_title_placeholder", "My App")}
                  maxLength="255"
                  disabled={loading}
                />
                <div className="form_hint">
                  <Message
                    id="publish_title_hint"
                    defaultMessage="The title shown in browser tabs and search results"
                  />
                </div>
              </div>

              <div className="form_group">
                <label htmlFor="pageDescription">
                  <Message id="publish_description_label" defaultMessage="Page Description" />
                </label>
                <textarea
                  id="pageDescription"
                  className="form-control"
                  value={pageDescription}
                  onChange={(e) => setPageDescription(e.target.value)}
                  placeholder={label(intl, "publish_description_placeholder", "A brief description of your app...")}
                  rows={3}
                  disabled={loading}
                />
                <div className="form_hint">
                  <Message
                    id="publish_description_hint"
                    defaultMessage="A short description for search engines and social media previews"
                  />
                </div>
              </div>

              <div className="form_actions">
                <button
                  type="submit"
                  className="btn bg-gradient-dark"
                  disabled={loading}
                >
                  {loading && <Loader2Icon size={16} className="spinning" />}
                  <Message id="publish_save" defaultMessage="Save" />
                </button>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="custom" pt="md">
            <CustomDomainWizard project={project} />
          </Tabs.Panel>
        </Tabs>
      </div>
    </div>
  );
};

export default PublishContent;
